<template>
  <div
    data-product-page
    class="flex h-app flex-col justify-stretch overflow-hidden desk:h-auto desk:min-h-screen desk:overflow-visible"
  >
    <div class="flex min-h-0 flex-grow flex-col justify-stretch">
      <Specification variant="page" height="h-full w-full" @close="close" :item="item" />
    </div>
    <AppFooter class="hidden flex-none desk:block" />
    <div class="h-14 flex-none desk:hidden"></div>
  </div>
</template>

<script>
import Specification from "@/components/product/Specification.vue";
import AppFooter from "@/components/app/AppFooter.vue";
import { canBackToCatalog } from "@/utils/desktopCatalog";
export default {
  components: {
    Specification,
    AppFooter,
  },
  auth: false,
  async asyncData({ $axios, params, error }) {
    try {
      const { data } = await $axios.get("/api/products/" + params.product);
      if (data && data.data) {
        return { item: data.data };
      }
    } catch (e) {
      const status = e.response && e.response.status;
      if (status && status !== 404) {
        return error({ statusCode: status });
      }
    }

    return error({ statusCode: 404, message: "找不到此商品" });
  },
  head() {
    const {name, description, image, no, price} = this.item;
    const keywords = this.$store.getters['app/info']?.keywords || "遊戲點數, 達人商城, 遊戲充值";
    const appName = this.$store.getters['app/info']?.name || process.env.APP_NAME;
    const title = `${name} | ${appName}`;
    const url = `${process.env.APP_URL}/order/product/${no}`;
    const cleanDescription = (description || "")
      .replace(/(<([^>]+)>)/gi, "")
      .substring(0, 150) + "...";

    const childProducts = this.item.products || [];
    const prices = childProducts.map(p => p.discount || p.price).filter(Boolean);
    const computedLowPrice = prices.length > 0
      ? Math.min(...prices)
      : (price || 0);
    const computedHighPrice = prices.length > 0
      ? Math.max(...prices)
      : (price || 0);
    const offerCount = childProducts.length || 1;
    const currency = this.currency || process.env.CURRECNY || 'MOP';

    // Build aggregateRating only when real review data exists
    const reviewsCount = this.item.reviews_count || 0;
    const reviewsAvg = this.item.reviews_avg_rating || 0;

    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "Product",
      name: name,
      description: cleanDescription,
      image: image ? [image] : [],
      offers: {
        "@type": "AggregateOffer",
        lowPrice: computedLowPrice,
        highPrice: computedHighPrice,
        offerCount: offerCount,
        priceCurrency: currency,
        availability: "https://schema.org/InStock",
        ...(childProducts.length > 0 && {
          offers: childProducts.map((p) => ({
            "@type": "Offer",
            name: p.name,
            price: p.discount || p.price,
            priceCurrency: currency,
            availability: p.stockout ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
            url: url,
          })),
        }),
      },
    };

    if (reviewsCount > 0) {
      jsonLd.aggregateRating = {
        "@type": "AggregateRating",
        ratingValue: reviewsAvg,
        bestRating: 5,
        worstRating: 1,
        ratingCount: reviewsCount,
      };

      // Inject real review entries for structured data
      const latestReviews = this.item.latest_reviews || [];
      if (latestReviews.length > 0) {
        jsonLd.review = latestReviews.map((r) => ({
          "@type": "Review",
          reviewRating: {
            "@type": "Rating",
            ratingValue: r.rating,
            bestRating: 5,
            worstRating: 1,
          },
          author: {
            "@type": "Person",
            name: r.customer?.name || "匿名用戶",
          },
          datePublished: r.created_at
            ? r.created_at.substring(0, 10)
            : undefined,
          ...(r.comment ? { reviewBody: r.comment } : {}),
        }));
      }
    }

    return {
      title: title,
      script: [
        {
          hid: "ldjson-schema",
          type: "application/ld+json",
          json: jsonLd,
        },
      ],
      link: [
        {
          rel: "canonical",
          href: url,
        },
      ],
      meta: [
        {
          hid: "robots",
          name: "robots",
          content: "index, follow",
        },
        {hid: "keywords", name: "keywords", content: `${name}, ${keywords}`},
        {hid: "description", name: "description", content: cleanDescription},
        {
          hid: "og:type",
          name: "og:type",
          property: "og:type",
          content: "product",
        },
        {
          hid: "og:title",
          name: "og:title",
          property: "og:title",
          content: title,
        },
        {
          hid: "og:site_name",
          name: "og:site_name",
          property: "og:site_name",
          content: appName,
        },
        {
          hid: "og:url",
          name: "og:url",
          property: "og:url",
          content: url,
        },
        {
          hid: "og:description",
          name: "og:description",
          property: "og:description",
          content: cleanDescription,
        },
        {
          hid: "og:image",
          name: "og:image",
          property: "og:image",
          content: image ?? `${process.env.APP_URL}/imgs/logo.png`,
        },
        {
          hid: "og:price:amount",
          name: "og:price:amount",
          property: "og:price:amount",
          content: computedLowPrice ? computedLowPrice.toString() : "",
        },
        {
          hid: "og:price:currency",
          name: "og:price:currency",
          property: "og:price:currency",
          content: currency,
        },
        {
          hid: "twitter:card",
          name: "twitter:card",
          content: "summary_large_image",
        },
        {
          hid: "twitter:title",
          name: "twitter:title",
          content: title,
        },
        {
          hid: "twitter:description",
          name: "twitter:description",
          content: cleanDescription,
        },
      ],
    };
  },
  methods: {
    close() {
      if (canBackToCatalog()) {
        this.$router.back();
        return;
      }
      this.$router.push(this.localePath({ name: 'order' }));
    },
  },
};
</script>
